import { useEffect, useRef, useState } from 'react';
import { Grid, GridCol, TextInput, Checkbox, Button, Group, Title } from '@mantine/core';
import ModalComponent from '../../../components/modal';
import { Language } from '../../../languages/languageHandler';
import { IAct, IEvent, addEvent, updateEvent, deleteEvent } from '../../../../libraries/helpdesk/events/events';
import { useStatusOperDialog, isZarphSuccessResponse } from '../../../context/status-oper-dialog';
import { StatusType } from '../../../../libraries/zps';
import './styles.scss';

interface ModalProps {
  mode: string;
  isOpen: boolean;
  hideBtn: () => void;
  lang: Language;
  event: IEvent;
}

const EditEventManagement = ({ mode, isOpen, hideBtn, lang, event }: ModalProps) => {
  const controller = useRef(new AbortController()).current;

  const [loading, setLoading] = useState(false);
  const [eventCode, setEventCode] = useState<number>(event?.eventCode);
  const [description, setDescription] = useState<string>(event?.description);
  const [selectedActs, setSelectedActs] = useState<IAct>(event?.act);

  const { handleApiError, showStatusOperDialog } = useStatusOperDialog();

  useEffect(() => {
    if (event?.act) {
      setSelectedActs(event.act);
    }
  }, [event]);

  useEffect(() => {
    setEventCode(event?.eventCode);
    setDescription(event?.description);
  }, [event]);

  const toggleAct = (key: keyof IAct) => {
    setSelectedActs(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      let response;

      if (mode === 'new') {
        response = await addEvent(controller, eventCode);
      } else if (mode === 'edit') {
        response = await updateEvent(controller, eventCode);
      } else if (mode === 'delete') {
        response = await deleteEvent(controller, eventCode);
      }

      if (isZarphSuccessResponse(response)) {
        const successMsg =
          mode === 'new'
            ? lang.ADD_EVENT_SUCCESS
            : mode === 'edit'
              ? lang.EDIT_EVENT_SUCCESS
              : lang.DELETE_EVENT_SUCCESS;

        showStatusOperDialog({
          type: StatusType.INFO,
          message: successMsg || 'Success',
          statusOper: response?.data?.statusOper
        });

        hideBtn(); // close modal
      } else {
        handleApiError(
          response,
          mode === 'new' ? lang.ADD_EVENT_ERROR : mode === 'edit' ? lang.EDIT_EVENT_ERROR : lang.DELETE_EVENT_ERROR
        );
      }
    } catch (error) {
      handleApiError(
        error,
        mode === 'new' ? lang.ADD_EVENT_ERROR : mode === 'edit' ? lang.EDIT_EVENT_ERROR : lang.DELETE_EVENT_ERROR
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <ModalComponent
        show={isOpen}
        handleClick={hideBtn}
        title={`${lang.EVENT_MANAGEMENT} - ${event.eventCode}`}
        content={
          <>
            <Grid>
              <GridCol span={{ base: 12 }}>
                <TextInput
                  label={lang.EVENT_CODE}
                  value={eventCode}
                  onChange={e => setEventCode(Number(e.target.value))}
                  readOnly={mode !== 'new'}
                />
              </GridCol>

              <GridCol span={{ base: 12 }}>
                <TextInput
                  label={lang.EQUIPMENT_DESCRIPTION}
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  readOnly={mode !== 'new'}
                />
              </GridCol>

              {selectedActs && (
                <>
                  <GridCol span={{ base: 12 }}>
                    <Title order={3}>{lang.EVENT_ACTUATION}</Title>
                  </GridCol>

                  {Object.entries(selectedActs).map(([key, value]) => (
                    <GridCol span={{ base: 12 }} key={key}>
                      <Checkbox
                        checked={value}
                        onChange={() => toggleAct(key as keyof IAct)}
                        label={lang[`EVENT_HELPDESK_${key.toUpperCase()}`] || key}
                      />
                    </GridCol>
                  ))}
                </>
              )}

              <Group className="buttons">
                <Button radius="md" onClick={handleSave} loading={loading}>
                  {lang.GLOBAL_SAVE_BUTTON}
                </Button>

                {mode === 'edit' && (
                  <Button className="delete" onClick={handleSave} loading={loading}>
                    {lang.GLOBAL_DELETE}
                  </Button>
                )}
              </Group>
            </Grid>
          </>
        }
      />
    </>
  );
};

export default EditEventManagement;
